import { useEffect, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import useAuth from '@sdk/authentication/useAuth';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import gettext from '@sdk/lang/gettext';
import ToggleGroup, {
  ToggleGroupOption,
  getToggleGroupOutcomePropertyValues,
} from '@sdk/components/forms/components/toggleGroup/ToggleGroup';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSection from '../SettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import SmallHelpBlock from '../SmallHelpBlock';
import { useUserSettings } from '../../UserSettingsContext';

const StyledColumn = styled(Column)`
  align-items: flex-end;
`;

const DashboardNotification: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { currentUser } = useAuth();
  const { userSettings, onUserSettingsChange } = useUserSettings();
  const { notificationsNotify, notificationsNotifyViaPush, notificationsNotifyViaEmail } =
    userSettings;

  const initializeToggleGroupOptions = (): ToggleGroupOption[] => {
    return [
      {
        name: 'notificationsNotifyViaEmail',
        label: 'Email',
        value: notificationsNotifyViaEmail,
        disabled: false,
      },
      {
        name: 'notificationsNotifyViaPush',
        label: 'Mobile',
        value: notificationsNotifyViaPush,
        disabled: !currentUser.canReceivePush,
      },
    ];
  };

  const [dashboardToggleGroupOptions, setDashboardToggleGroupOptions] = useState(() =>
    initializeToggleGroupOptions()
  );

  useEffect(() => {
    setDashboardToggleGroupOptions(initializeToggleGroupOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsNotifyViaPush, notificationsNotifyViaEmail]);

  const onChangeHandler = (value: ToggleGroupOption[]): void => {
    setDashboardToggleGroupOptions(value);
    onUserSettingsChange(getToggleGroupOutcomePropertyValues(value));
  };

  return (
    <SettingsSection>
      <CollapsibleSettingsSection
        isActive={!!notificationsNotify}
        headerItem={
          <SettingsSwitcherRow
            withSeparator
            value={Boolean(notificationsNotify)}
            onChange={(value): void => onUserSettingsChange({ notificationsNotify: value ? 1 : 0 })}
            subtitle={gettext(
              'Dashboard notifications include shift updates, tasks, and announcements relevant to you. By default, these notifications are displayed on your Dashboard.'
            )}
          />
        }
        contentItem={
          <>
            <Column pBottom={20}>
              <span>
                <Text14 themeColor={ThemeColorType.gray}>
                  {gettext('Always send me additional alerts')}&nbsp;
                </Text14>
                <Text14 bold themeColor={ThemeColorType.gray}>
                  {gettext('when there is a new notification on my dashboard:')}
                </Text14>
              </span>
            </Column>

            <Column pBottom={20}>
              <RowSpaceBetween>
                <Text14 themeColor={ThemeColorType.gray}>
                  {gettext('Send notification via:')}
                </Text14>
                <StyledColumn>
                  <ToggleGroup onChange={onChangeHandler} options={dashboardToggleGroupOptions} />
                  {!currentUser.canReceivePush && <SmallHelpBlock />}
                </StyledColumn>
              </RowSpaceBetween>
            </Column>
          </>
        }
      />
    </SettingsSection>
  );
};

export default DashboardNotification;
